// @ts-nocheck
import { Box, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import moment from 'moment';

import { FHButton, FHTooltip, FHWarning } from '../../../../components/base';
import { FAutocomplete, FDateTimePicker, FInput, FSelect, FTimePicker } from '../../../../components/baseV2';
import { BcbaAttendance, Roles } from '../../../../constants';
import {
  areUserRolesIn,
  capitalizeFirstLetter,
  combineMomentDateTime,
  disabledDatesOutsideRange,
  disabledFutureHoursAndMinutes,
} from '../../../../helpers';
import { FORTA_BLACK, FORTA_ERROR, FORTA_WHITE } from '../../../../utils/colors';
import TrashIcon from '../../../../components/svgs/TrashIcon3';
import {
  DeliveryMethods,
  Durations,
  DURATION_PLACEHOLDER_VALUE,
  Locations,
  newLocations,
} from '../constants';
import { getSessionUnitsAfterBreaks } from '../utils';
import {
  BAY_WARNING_CONTAINER_SX,
  BAY_WARNING_TEXT_SX,
  CLIENT_OPTION_DESCRIPTION_SX,
  DELETE_BUTTON_SX,
  DELETE_BUTTON_WRAPPER_SX,
  EVV_VERIFICATION_BUTTON_SX,
  EVV_VERIFICATION_BUTTON_WRAPPER_SX,
  FORM_TOOLTIP_SX,
  NEW_SESSION_GRID_SX,
  NEW_SESSION_INFO_CONTENT_SX,
  NEW_SESSION_INFO_ROOT_SX,
  NEW_SESSION_OUTLINED_BLACK_BORDER_SX,
  NEW_SESSION_SELECT_BLACK_BORDER_SX,
  NEW_SESSION_TOP_GRID_SX,
  START_BUTTON_SX,
  START_BUTTON_WRAPPER_SX,
  TIME_TOOLTIP_SX,
  WARNING_WRAPPER_SX,
} from '../styles';

import type { Moment } from 'moment';
import type { FocusEvent, HTMLAttributes, SyntheticEvent } from 'react';
import type { IOption, SelectValueType } from '../../../../utils/types';
import type { INewSessionInfoFieldsProps } from '../types';

export default function NewSessionInfoFields({
  bcbaAttendanceStatus,
  clientOptions,
  cohortAssignmentLoading,
  currentClientCohortAssignments,
  deliveryMethod,
  deliveryMethodOptions,
  disableByClientBay,
  durationLabels,
  durationToolTipOpen,
  endBetween10to12,
  handleBcbaAttendanceChange,
  handleClientChange,
  handleDeliveryMethodChange,
  handleGetOutOnRangeCount,
  handleStartEvvVerification,
  handleStartSession,
  handleTimeRangeUpdate,
  handleTimeSelect,
  handleUpdateLocation,
  handleUpdateSessionTime,
  isEditable,
  isStartEvvPending,
  location,
  narrativeSuggestionLoading,
  selectedClientId,
  selectedDuration,
  session,
  sessionLoading,
  sessionStartDate,
  setOpenClientConfirm,
  setPendingSelectedClientId,
  setShowConfirmDeleteDialog,
  setShowRateEventWarning,
  showBcbaAttendanceField,
  showDeleteButton,
  startBetween10to12,
  evvStartLoading,
  startSessionLoading,
  timeStartValue,
  timeToolTipOpen,
  tooltipTitle,
  user,
}: INewSessionInfoFieldsProps) {
  const unitsAfterBreaks = selectedDuration
    ? getSessionUnitsAfterBreaks(selectedDuration, session?.breaks ?? [])
    : 0;
  const topRowItemSx = {
    '@media (min-width: 1024px)': {
      flexBasis: showBcbaAttendanceField ? '20%' : '25%',
      maxWidth: showBcbaAttendanceField ? '20%' : '25%',
    },
  };
  const sessionDetailsRowItemSx = {
    '@media (min-width: 1024px)': {
      flexBasis: '20%',
      maxWidth: '20%',
    },
  };
  const desktopEvvTopRowItemSx = {
    display: 'none',
    '@media (min-width: 1024px)': {
      display: 'block',
      flexBasis: showBcbaAttendanceField ? '20%' : '25%',
      maxWidth: showBcbaAttendanceField ? '20%' : '25%',
    },
  };
  const mobileStartActionSx = {
    '@media (min-width: 1024px)': {
      display: 'none',
    },
  };
  const mobileEvvDeleteActionsSx = {
    display: 'flex',
    gap: 2,
    '@media (min-width: 1024px)': {
      display: 'none',
    },
  };
  const pendingDesktopDeleteWrapperSx = {
    ...DELETE_BUTTON_WRAPPER_SX,
    display: 'none',
    '@media (min-width: 1024px)': {
      display: 'flex',
    },
  };
  const evvDeleteButtonSx = {
    ...DELETE_BUTTON_SX,
    flex: 1,
    minWidth: 0,
    height: 40,
    ml: 0,
    justifyContent: 'center',
  };

  const handleClientSelect = (_event: SyntheticEvent, newValue: IOption | null) => {
    const nextClientId = newValue?.id == null ? null : Number(newValue.id);
    setPendingSelectedClientId(nextClientId);
    if (selectedClientId) {
      setOpenClientConfirm(true);
      return;
    }
    handleClientChange(nextClientId);
  };

  const isStartDisabled =
    !selectedDuration ||
    timeToolTipOpen ||
    durationToolTipOpen ||
    cohortAssignmentLoading ||
    disableByClientBay;

  const handleDateBlur = (event: FocusEvent<HTMLInputElement>) => {
    handleUpdateSessionTime(
      moment(
        timeStartValue
          ? combineMomentDateTime(moment(event.target.value), moment(timeStartValue))
          : moment(event.target.value),
        'MM/DD/YYYY - hh:mm A',
      ),
    );
  };

  const handleDateChange = (value: Moment | null) => {
    if (!value) return;

    const outsideRageCount = handleGetOutOnRangeCount(moment(value));
    if (outsideRageCount !== null && outsideRageCount > 0) {
      setShowRateEventWarning(true);
      return;
    }
    handleTimeRangeUpdate('date_start', value);
  };

  const handleTimeBlur = () => {
    handleUpdateSessionTime(
      timeStartValue
        ? combineMomentDateTime(moment(sessionStartDate), moment(timeStartValue))
        : moment(sessionStartDate),
    );
  };

  const handleTimeOk = (newValue: Moment) => {
    handleUpdateSessionTime(
      timeStartValue
        ? combineMomentDateTime(moment(newValue), moment(timeStartValue))
        : moment(newValue),
    );
  };

  return (
    <Box sx={NEW_SESSION_INFO_ROOT_SX}>
      <Box sx={NEW_SESSION_INFO_CONTENT_SX}>
        <Grid
          container
          columnSpacing={{ xs: 2.5, md: 3 }}
          rowSpacing={{ xs: 3, md: 3.5 }}
          alignItems="flex-start"
          sx={NEW_SESSION_TOP_GRID_SX}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={showBcbaAttendanceField ? 3 : 3.5}
            lg={2.5}
            sx={topRowItemSx}
          >
            <FAutocomplete
              data-testid="clientNameDropDownSessionNotes"
              label="Client name"
              required
              fullWidth
              placeholder="Select client"
              clearIcon={false}
              getOptionLabel={(option: IOption) => option.label}
              isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
              renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => (
                <Box
                  {...props}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                  key={option.id}
                  data-testid={`clientLabel${option.id}`}
                >
                  <Typography>{option.label}</Typography>
                  {option.description && (
                    <Typography sx={CLIENT_OPTION_DESCRIPTION_SX}>
                      {capitalizeFirstLetter(option.description)}
                    </Typography>
                  )}
                </Box>
              )}
              disabled={!!session || narrativeSuggestionLoading}
              options={clientOptions}
              value={
                clientOptions.length > 0 && selectedClientId
                  ? (clientOptions.find((cl) => cl.id === selectedClientId) ?? null)
                  : null
              }
              onChange={handleClientSelect}
            />
          </Grid>
          {showBcbaAttendanceField && (
            <Grid item xs={12} sm={6} md={3} lg={2.5} sx={topRowItemSx}>
              <Box sx={NEW_SESSION_SELECT_BLACK_BORDER_SX}>
                <FSelect
                  data-testid="bcbaAttendanceDropDownSessionNotes"
                  label="BCBA attendance"
                  required
                  fullWidth
                  placeholder="Select if BCBA attend"
                  disabled={(session && !isEditable) || narrativeSuggestionLoading}
                  options={Object.values(BcbaAttendance).map((value) => ({
                    value,
                    label: value,
                  }))}
                  optionDataTestIdPrefix="bcbaAttendanceItemSessionNotes"
                  value={
                    bcbaAttendanceStatus === true
                      ? BcbaAttendance.Present
                      : bcbaAttendanceStatus === false
                        ? BcbaAttendance.Absent
                        : ''
                  }
                  onUpdate={(value: SelectValueType) => handleBcbaAttendanceChange(String(value))}
                />
              </Box>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={6}
            md={showBcbaAttendanceField ? 3 : 3.5}
            lg={2.5}
            sx={topRowItemSx}
          >
            <Box sx={NEW_SESSION_SELECT_BLACK_BORDER_SX}>
              <FSelect
                data-testid="locationDropDownSessionNotes"
                label="Location"
                required
                fullWidth
                placeholder="Select location"
                disabled={(session && !isEditable) || narrativeSuggestionLoading}
                options={Object.values(newLocations).map((value) => ({
                  value,
                  label: value,
                }))}
                optionDataTestIdPrefix="locationItemSessionNotes"
                value={location}
                onUpdate={(value: SelectValueType) => {
                  const locationValue = Object.values(Locations).find((item) => item === value);
                  if (locationValue) handleUpdateLocation(locationValue);
                }}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={showBcbaAttendanceField ? 3 : 3.5}
            lg={2.5}
            sx={topRowItemSx}
          >
            <Box sx={NEW_SESSION_SELECT_BLACK_BORDER_SX}>
              <FSelect
                data-testid="deliveryMethodDropDownSessionNotes"
                label="Delivery method"
                fullWidth
                disabled={(session && !isEditable) || narrativeSuggestionLoading}
                options={deliveryMethodOptions}
                optionDataTestIdPrefix="deliveryMethodItemSessionNotes"
                value={deliveryMethod}
                  onUpdate={(value: SelectValueType) => {
                    const deliveryMethodValue = Object.values(DeliveryMethods).find(
                      (item) => item === value,
                    );
                    if (deliveryMethodValue) handleDeliveryMethodChange(deliveryMethodValue);
                  }}
              />
            </Box>
          </Grid>
          {user?.roles &&
            !sessionLoading &&
            (!session || isStartEvvPending) &&
            areUserRolesIn(user.roles, [Roles.Prbt, Roles.Vbt, Roles.Bcba]) && (
              <Grid item xs={12} sm={6} md={3} lg={2} sx={desktopEvvTopRowItemSx}>
                <Box sx={EVV_VERIFICATION_BUTTON_WRAPPER_SX}>
                  <FHButton
                    data-testid="startSessionNotes"
                    sx={EVV_VERIFICATION_BUTTON_SX}
                    loading={isStartEvvPending ? evvStartLoading : startSessionLoading}
                    disabled={!isStartEvvPending && isStartDisabled}
                    onClick={
                      isStartEvvPending ? handleStartEvvVerification : handleStartSession
                    }
                  >
                    {isStartEvvPending ? 'EVV Verification' : 'Start'}
                  </FHButton>
                </Box>
                {!isStartEvvPending &&
                  areUserRolesIn(user.roles, [Roles.Bcba]) &&
                  currentClientCohortAssignments &&
                  !cohortAssignmentLoading &&
                  selectedClientId &&
                  disableByClientBay && (
                    <Box sx={WARNING_WRAPPER_SX}>
                      <FHWarning
                        bold
                        iconColor={FORTA_ERROR}
                        sxText={BAY_WARNING_TEXT_SX}
                        sxContainer={BAY_WARNING_CONTAINER_SX}
                        content={`BCBAs are not allowed to create sessions for ${currentClientCohortAssignments.cohort?.name} clients.`}
                      />
                    </Box>
                  )}
                {!isStartEvvPending && cohortAssignmentLoading && (
                  <CircularProgress sx={{ mt: 1, mr: 5 }} size={16} />
                )}
              </Grid>
            )}
        </Grid>

        <Grid
          container
          columnSpacing={{ xs: 2.5, md: 3 }}
          rowSpacing={{ xs: 3, md: 3.5 }}
          alignItems="flex-start"
          sx={NEW_SESSION_GRID_SX}
        >
          <Grid item xs={12} sm={6} md={3} lg={2.5} sx={sessionDetailsRowItemSx}>
            <FDateTimePicker
              data-testid="sessionNoteDatePicker"
              label="Session start date"
              required
              fullWidth
              dateOnly
              format="MM/DD/YY"
              allowClear={false}
              disabledDate={(current: Moment) => disabledDatesOutsideRange(current, -14, 0)}
              disabled={(session && !isEditable) || narrativeSuggestionLoading || !selectedClientId}
              value={sessionStartDate ? moment(sessionStartDate) : null}
              onBlur={handleDateBlur}
              onChange={handleDateChange}
              onSelect={handleDateChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5} sx={sessionDetailsRowItemSx}>
            <FHTooltip
              sx={TIME_TOOLTIP_SX}
              title={tooltipTitle}
              arrow
              open={timeToolTipOpen || startBetween10to12}
              placement="top"
              maxwidth={340}
              tooltipBorderRadius="8px"
              tooltipbackground={FORTA_WHITE}
              tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
            >
              <Box component="span" sx={NEW_SESSION_OUTLINED_BLACK_BORDER_SX}>
                <FTimePicker
                  data-testid="sessionNoteTimePicker"
                  label="Session start time"
                  required
                  fullWidth
                  format="h:mm A"
                  showClockIcon
                  allowClear={false}
                  showNow
                  use12Hours
                  outlineColor={FORTA_BLACK}
                  disabled={
                    (session && !isEditable) || narrativeSuggestionLoading || !sessionStartDate
                  }
                  disabledTime={() =>
                    disabledFutureHoursAndMinutes(
                      combineMomentDateTime(
                        moment(sessionStartDate),
                        moment(timeStartValue ?? undefined),
                      ),
                    )
                  }
                  value={timeStartValue ? moment(timeStartValue) : null}
                  onBlur={handleTimeBlur}
                  onOk={handleTimeOk}
                  onChange={handleTimeSelect}
                  onSelect={handleTimeSelect}
                  error={timeToolTipOpen || startBetween10to12}
                />
              </Box>
            </FHTooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2.5} sx={sessionDetailsRowItemSx}>
            <FHTooltip
              sx={FORM_TOOLTIP_SX}
              title={tooltipTitle}
              arrow
              open={durationToolTipOpen || endBetween10to12}
              placement="top"
              maxwidth={340}
              tooltipBorderRadius="8px"
              tooltipbackground={FORTA_WHITE}
              tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
            >
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={NEW_SESSION_SELECT_BLACK_BORDER_SX}>
                  <FSelect
                    data-testid="durationDropDownSessionNotes"
                    label="Duration"
                    required
                    fullWidth
                    placeholder="Select duration"
                    disabled={(session && !isEditable) || narrativeSuggestionLoading}
                    options={[
                      {
                        value: DURATION_PLACEHOLDER_VALUE,
                        label: 'Select duration',
                        disabled: true,
                      },
                      ...Object.keys(Durations).map((label) => ({ value: label, label })),
                    ]}
                    optionDataTestIdPrefix="durationItemSessionNotes"
                    value={selectedDuration ? durationLabels[String(selectedDuration)] : ''}
                    onUpdate={(value: SelectValueType) => {
                      const duration = Object.entries(Durations).find(([label]) => label === value)?.[1];
                      if (duration !== undefined) handleTimeRangeUpdate('expected_duration', duration);
                    }}
                    error={durationToolTipOpen || endBetween10to12}
                  />
                </Box>
              </Box>
            </FHTooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2} sx={sessionDetailsRowItemSx}>
            <FInput
              label="Units"
              fullWidth
              value={unitsAfterBreaks}
              disabled
            />
          </Grid>

          {user?.roles && !sessionLoading && (
            <Grid
              item
              xs={12}
              sm={!session || isStartEvvPending ? 12 : 6}
              md={!session || isStartEvvPending ? 12 : 3}
              lg={2}
              sx={sessionDetailsRowItemSx}
            >
              {areUserRolesIn(user.roles, [Roles.Prbt, Roles.Vbt, Roles.Bcba]) && !session && (
                <Box sx={mobileStartActionSx}>
                  <Box sx={START_BUTTON_WRAPPER_SX}>
                    <FHButton
                      data-testid="startSessionNotes"
                      sx={START_BUTTON_SX}
                      loading={startSessionLoading}
                      disabled={isStartDisabled}
                      onClick={handleStartSession}
                    >
                      Start
                    </FHButton>
                  </Box>
                  {areUserRolesIn(user.roles, [Roles.Bcba]) &&
                    currentClientCohortAssignments &&
                    !cohortAssignmentLoading &&
                    selectedClientId &&
                    disableByClientBay && (
                      <Box sx={WARNING_WRAPPER_SX}>
                        <FHWarning
                          bold
                          iconColor={FORTA_ERROR}
                          sxText={BAY_WARNING_TEXT_SX}
                          sxContainer={BAY_WARNING_CONTAINER_SX}
                          content={`BCBAs are not allowed to create sessions for ${currentClientCohortAssignments.cohort?.name} clients.`}
                        />
                      </Box>
                  )}
                  {cohortAssignmentLoading && <CircularProgress sx={{ mt: 1, mr: 5 }} size={16} />}
                </Box>
              )}
              {isStartEvvPending &&
                areUserRolesIn(user.roles, [Roles.Prbt, Roles.Vbt, Roles.Bcba]) && (
                  <Box sx={mobileEvvDeleteActionsSx}>
                    <FHButton
                      data-testid="mobileEvvVerificationButton"
                      sx={{ ...EVV_VERIFICATION_BUTTON_SX, flex: 1, minWidth: 0 }}
                      loading={evvStartLoading}
                      onClick={handleStartEvvVerification}
                    >
                      EVV Verification
                    </FHButton>
                    {showDeleteButton && (
                      <IconButton
                        sx={evvDeleteButtonSx}
                        onClick={() => setShowConfirmDeleteDialog(true)}
                        data-testid="deleteNoteButton"
                      >
                        <TrashIcon color={FORTA_ERROR} width={18} height={18} />
                        <Typography ml={1}>Delete</Typography>
                      </IconButton>
                    )}
                  </Box>
                )}
              {showDeleteButton && (
                <Box
                  sx={
                    isStartEvvPending
                      ? pendingDesktopDeleteWrapperSx
                      : DELETE_BUTTON_WRAPPER_SX
                  }
                >
                  <IconButton
                    sx={isStartEvvPending ? evvDeleteButtonSx : DELETE_BUTTON_SX}
                    onClick={() => setShowConfirmDeleteDialog(true)}
                    data-testid="deleteNoteButton"
                  >
                    <TrashIcon color={FORTA_ERROR} width={18} height={18} />
                    <Typography ml={1}>Delete</Typography>
                  </IconButton>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
